import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, LogOut, Search, ChevronRight, ChevronDown, Palette, Check } from "lucide-react";

export function cn(...a) { return a.filter(Boolean).join(" "); }

export function fmtINR(n) {
  const v = Number(n || 0);
  return "₹" + v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}
export function fmtNum(n) { return Number(n || 0).toLocaleString("en-IN"); }
export function fmtDate(d) { if (!d) return "—"; try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }); } catch { return "—"; } }
export function fmtDateTime(d) { if (!d) return "—"; try { return new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true, timeZone: "Asia/Kolkata" }) + " IST"; } catch { return "—"; } }

const ACCENTS = {
  emerald: { ring: "#10B981", text: "text-emerald-400", bg: "bg-emerald-500", hov: "hover:bg-emerald-400", soft: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  indigo: { ring: "#6366F1", text: "text-indigo-400", bg: "bg-indigo-500", hov: "hover:bg-indigo-400", soft: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30" },
  teal: { ring: "#14B8A6", text: "text-teal-400", bg: "bg-teal-500", hov: "hover:bg-teal-400", soft: "bg-teal-500/10 text-teal-300 border-teal-500/30" },
  sky: { ring: "#0EA5E9", text: "text-sky-400", bg: "bg-sky-500", hov: "hover:bg-sky-400", soft: "bg-sky-500/10 text-sky-300 border-sky-500/30" },
};
export const accent = (a) => ACCENTS[a] || ACCENTS.emerald;

export const THEMES = [
  { id: "warm-premium", name: "Warm Luxury", dot: "#C89566" },
  { id: "slate-light", name: "Swiss Light", dot: "#F97316" },
  { id: "emerald-dark", name: "Emerald Dark", dot: "#10B981" },
  { id: "midnight-luxe", name: "Midnight Gold", dot: "#FACC15" },
];
export function getSavedTheme() {
  try { return localStorage.getItem("trackflow_selected_theme") || "warm-premium"; } catch { return "warm-premium"; }
}
export function applyTheme(id) {
  document.documentElement.setAttribute("data-theme", id);
  try { localStorage.setItem("trackflow_selected_theme", id); } catch { /* ignore */ }
}
export function ThemeSwitcher() {
  const [open, setOpen] = useState(false);
  const [cur, setCur] = useState(getSavedTheme());
  const pick = (id) => { applyTheme(id); setCur(id); setOpen(false); };
  return (
    <div className="relative">
      <button data-testid="theme-switcher-button" onClick={() => setOpen((o) => !o)} title="Change theme"
        className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--tf-border)] text-[var(--tf-text-muted)] transition-colors hover:text-[var(--tf-text-main)]">
        <Palette size={16} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-2 w-48 rounded-xl border border-[var(--tf-border)] bg-[var(--tf-card)] p-1.5 shadow-xl">
            {THEMES.map((t) => (
              <button key={t.id} data-testid={`theme-option-${t.id}`} onClick={() => pick(t.id)}
                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-[var(--tf-text-main)] hover:bg-[var(--tf-card-hover)]">
                <span className="h-3.5 w-3.5 rounded-full" style={{ background: t.dot }} />
                <span className="flex-1 text-left">{t.name}</span>
                {cur === t.id && <Check size={14} className="text-[var(--tf-primary)]" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function Btn({ variant = "primary", className = "", acc = "emerald", children, ...p }) {
  const A = accent(acc);
  const base = "inline-flex items-center justify-center gap-2 font-medium rounded-lg px-4 py-2.5 text-sm transition-colors active:scale-95 disabled:opacity-50 disabled:pointer-events-none";
  const styles = {
    primary: `${A.bg} ${A.hov} text-white shadow-sm`,
    secondary: "bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700",
    ghost: "hover:bg-slate-800 text-slate-300 hover:text-white",
    danger: "bg-rose-600 hover:bg-rose-500 text-white",
    outline: "border border-slate-600 hover:border-slate-400 text-slate-200",
  };
  return <button className={cn(base, styles[variant], className)} {...p}>{children}</button>;
}

const BADGE = {
  active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  live: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  stopped: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  successful: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  trial: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  grace: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  new: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  draft: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  paused: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  open: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  in_progress: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  resolved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  rejected: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  failed: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  expired: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  suspended: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  blocked: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  invalid: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  duplicate: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  flagged: "bg-orange-500/15 text-orange-300 border-orange-500/30",
};
export function Badge({ status, children, className = "" }) {
  const key = (status || "").toLowerCase();
  const style = BADGE[key] || "bg-slate-600/20 text-slate-300 border-slate-600/40";
  return <span data-testid={`badge-${key}`} className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize", style, className)}>{children || (status || "").replace(/_/g, " ")}</span>;
}

export function Card({ className = "", children, ...p }) {
  return <div className={cn("tf-card p-6", className)} {...p}>{children}</div>;
}

export function StatCard({ label, value, sub, icon: Icon, acc = "emerald", testid }) {
  const A = accent(acc);
  return (
    <motion.div whileHover={{ y: -2 }} className="tf-card p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p>
          <p className="mt-2 font-head text-2xl font-bold text-slate-50 tabular">{value}</p>
          {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
        </div>
        {Icon && <div className={cn("rounded-lg p-2.5", A.soft, "border")}><Icon size={18} className={A.text} /></div>}
      </div>
    </motion.div>
  );
}

export function PageTitle({ title, subtitle, actions }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="font-head text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-400">{subtitle}</p>}
      </div>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
  );
}

export function Table({ columns, rows, empty = "No records found.", testid, rowClass }) {
  return (
    <div className="tf-card overflow-hidden p-0" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-800 bg-slate-900/60 text-left">
              {columns.map((c, i) => (
                <th key={i} className={cn("px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-400", c.align === "right" && "text-right", c.align === "center" && "text-center")}>{c.header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-slate-500">{empty}</td></tr>
            ) : rows.map((r, ri) => (
              <tr key={ri} className={cn("border-b border-slate-800/60 transition-colors hover:bg-slate-800/30", rowClass && rowClass(r))}>
                {columns.map((c, ci) => (
                  <td key={ci} className={cn("px-4 py-3 text-slate-200", c.align === "right" && "text-right tabular", c.align === "center" && "text-center", c.mono && "font-mono")}>
                    {c.render ? c.render(r) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Modal({ open, onClose, title, children, size = "max-w-lg" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.96, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className={cn("tf-card my-8 w-full p-6", size)} onClick={(e) => e.stopPropagation()} data-testid="modal">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-head text-lg font-semibold text-slate-50">{title}</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white" data-testid="modal-close"><X size={18} /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
    </label>
  );
}

export function Shell({ brand, accentColor = "emerald", nav, active, onNav, user, onLogout, children, right }) {
  const [open, setOpen] = useState(false);
  useEffect(() => { applyTheme(getSavedTheme()); }, []);
  const activeGroupIdx = nav.findIndex((e) => e.items && e.items.some((it) => it.key === active));
  const [openGroup, setOpenGroup] = useState(activeGroupIdx === -1 ? 0 : activeGroupIdx);
  useEffect(() => { if (activeGroupIdx !== -1) setOpenGroup(activeGroupIdx); }, [activeGroupIdx]);
  const A = accent(accentColor);
  const NavList = () => {
    const renderItem = (item) => {
      const Icon = item.icon;
      const act = active === item.key;
      return (
        <button key={item.key} data-testid={`nav-${item.key}`} onClick={() => { onNav(item.key); setOpen(false); }}
          className={cn("group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
            act ? cn(A.soft, "border") : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-100")}>
          {Icon && <Icon size={17} className={act ? A.text : "text-slate-500 group-hover:text-slate-300"} />}
          <span className="flex-1 text-left">{item.label}</span>
          {act && <ChevronRight size={14} className={A.text} />}
        </button>
      );
    };
    return (
      <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-2">
        {nav.map((entry, gi) => entry.items ? (
          <div key={gi} className="mb-1">
            <button onClick={() => setOpenGroup((g) => g === gi ? -1 : gi)}
              className="flex w-full items-center justify-between rounded px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-slate-600 hover:text-slate-400" data-testid={`navgroup-${gi}`}>
              <span>{entry.group}</span>
              <ChevronDown size={12} className={cn("transition-transform", openGroup !== gi && "-rotate-90")} />
            </button>
            {openGroup === gi && <div className="space-y-0.5">{entry.items.map(renderItem)}</div>}
          </div>
        ) : renderItem(entry))}
      </nav>
    );
  };
  return (
    <div className="flex min-h-screen bg-[var(--tf-bg)]">
      {/* desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-[var(--tf-border)] bg-[var(--tf-sidebar-bg)] lg:flex">
        <div className="flex items-center gap-2 px-5 py-5">
          {brand.logo ? (
            <img src={brand.logo} alt={brand.title} className="h-10 w-auto max-w-[190px] object-contain" />
          ) : (
            <>
              <div className={cn("h-8 w-8 rounded-lg", A.bg)} style={{ boxShadow: `0 0 18px ${A.ring}66` }} />
              <div>
                <p className="font-head text-sm font-bold leading-tight text-slate-50">{brand.title}</p>
                <p className="text-[11px] text-slate-500">{brand.subtitle}</p>
              </div>
            </>
          )}
        </div>
        <NavList />
        <div className="border-t border-slate-800 p-3">
          <div className="mb-2 px-2">
            <p className="truncate text-sm font-medium text-slate-200">{user?.name}</p>
            {user?.role !== "super_admin" && <p className="truncate text-xs text-slate-500">{user?.email}</p>}
          </div>
          <Btn variant="ghost" acc={accentColor} className="w-full justify-start" onClick={onLogout} data-testid="logout-btn"><LogOut size={16} />Log out</Btn>
        </div>
      </aside>

      {/* mobile */}
      {open && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}
      <aside className={cn("fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-slate-800 bg-slate-950 transition-transform lg:hidden", open ? "translate-x-0" : "-translate-x-full")}>
        <div className="flex items-center justify-between px-5 py-5">
          {brand.logo ? <img src={brand.logo} alt={brand.title} className="h-9 w-auto max-w-[150px] object-contain" /> : <p className="font-head text-sm font-bold text-slate-50">{brand.title}</p>}
          <button onClick={() => setOpen(false)} className="text-slate-400"><X size={18} /></button>
        </div>
        <NavList />
        <div className="border-t border-slate-800 p-3">
          <Btn variant="ghost" acc={accentColor} className="w-full justify-start" onClick={onLogout}><LogOut size={16} />Log out</Btn>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-[var(--tf-border)] bg-[var(--tf-header-bg)] px-4 py-3 backdrop-blur-md lg:px-8">
          <button className="rounded-lg p-2 text-slate-300 hover:bg-slate-800 lg:hidden" onClick={() => setOpen(true)}><Menu size={20} /></button>
          <div className="flex-1" />
          {right}
          <ThemeSwitcher />
          <div className={cn("flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold text-white", A.bg)}>
            {(user?.name || "?").slice(0, 1).toUpperCase()}
          </div>
        </header>
        <main className="flex-1 px-4 py-6 lg:px-8 lg:py-8">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
}
